'use client'

import { useContext, useState, useEffect } from 'react'
import { AppContext } from "../context/Context";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { manrope } from "@/ui/fonts";
import { Carousel } from "@material-tailwind/react";
import { updateCart, deleteCartItem, addToCart } from "@/utils/cart/cartUtils";
import { v4 as uuid } from 'uuid';
import ArrowsLeftHeaderPopUp from '../svg/ArrowLeftPopUpCart';
import ArrowRightHeaderPopUp from '../svg/ArrowRightPopUpCart';
import { checkDiscountValidation } from '@/utils/coupons/applyDiscount';

export default function CartPopUp({scrolled}) {
	const [cartWP, setCartWP] = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [updatingProduct, setUpdatingProduct] = useState(false);
  const [removingProduct, setRemovingProduct] = useState(false);
  const [open, setOpen] = useState(false)
  const [products, setProducts] = useState({ products: [] });
  const [accesorios, setAccesorios] = useState([]);
  const [price, setPrice] = useState(0);
  const [cart, setCart] = useState({ totalQty: 0 });
  const [selectedVariations, setSelectedVariations] = useState({});

  const handleSelectVariation = (productId, variation) => {
    setSelectedVariations(prev => ({
      ...prev,
      [productId]: variation, // Store variation per product
    }));
  };

	useEffect(() => {
			const newSubtotal = products.products.reduce((total, product) => {
				const productPrice = parseFloat(product.price) || 0;
				return total + productPrice * product.quantity;
			}, 0);
			setPrice(newSubtotal);
		}, [products]);

	
	useEffect(() => {
	const fetchCart = async () => {
		const session_id = localStorage.getItem("session_id");
		if (!session_id) return;

		try {
			const response = await fetch(`/api/get_products_for_cart?session_id=${session_id}`);
			if (response.ok) {
				const data = await response.json();
				setProducts(data);
				// Sum the quantities
				const totalQuantity = data.products.reduce((sum, item) => sum + item.quantity, 0);

				// Update state
				setCart({ totalQty: totalQuantity });


				// Dispatch custom event
				const cartUpdateEvent = new CustomEvent("cartUpdated", { detail: { totalQty: totalQuantity } });
				window.dispatchEvent(cartUpdateEvent);
			} else {
				console.error("Failed to fetch cart");
			}
		} catch (error) {
			console.error("Error fetching cart data:", error);
		}
	};

	const refreshCartListener = () => {
    fetchCart();
    setOpen(true)
  };

	window.addEventListener("refreshCart", refreshCartListener);

	fetchCart(); // Initial fetch

	return () => window.removeEventListener("refreshCart", refreshCartListener);
}, []);

useEffect(() => {
  const fetchAccesorios = async () => {
    const response = await fetch(`/api/get_products_woocommerce`);
    if (response.ok) {
      const product_woocommerce = await response.json();
      const accesories = product_woocommerce.filter(product =>
        product.categories.some(category => category.name === "Accesorios")
      );
      setAccesorios((prevAccesorios) => {
        return accesories.filter(
          (accesorio) =>
            !products.products.some((product) => product.product_id === accesorio.id && accesorio.type !== "variable")
        );
      });
    }
  };

  fetchAccesorios();
}, [products.products]); // Add dependency to ensure filtering happens when products update


	const handleRemoveProductClick = async (event, id, product_id) => {
			event.stopPropagation();
		
			try {
		
				// Replace with your actual API endpoint
				const apiEndpoint = `/api/delete_product?id=${id}`;
		
				const response = await fetch(apiEndpoint, {
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
					},
				});
		
				if (!response.ok) {
					throw new Error('Failed to delete item');
				}

        // Check discount validation
        const session_id = localStorage.getItem("session_id");
        await checkDiscountValidation(session_id, setDiscount)
		
				// Remove the product from the parent component state
        if (products.length) {
          cartWP.map((product) => {
            if (product_id === product?.product_id || product_id === product?.variation_id) {
				      deleteCartItem(cartKey, setCartWP, setRemovingProduct);
            }
          })
        }
			} catch (error) {
				console.error('Error removing product:', error);
			} finally {
				// Refresh the cart in the Header component
				window.dispatchEvent(new Event("refreshCart"));
			}
		};

	const separadorDeMiles = (numero) => {
			let partesNumero = numero.toString().split(".");
			partesNumero[0] = partesNumero[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
			return partesNumero.join(".");
	};

	const handleQtyChange = async (event, type, qty, product_id, id) => {
    event.stopPropagation();
  
    let newQty = qty;
  
    if (type === "increment") {
      newQty += 1;
    } else if (type === "decrement") {
      if (newQty > 1) newQty -= 1;
      else return;
    } else {
      newQty = parseInt(event.target.value) || 1;
    }
  
  
    try {
      if (products.length) {
        cartWP.map((product) => {
          if (product_id === product?.product_id || product_id === product?.variation_id) {
            updateCart(product?.key, newQty, setCartWP, setUpdatingProduct, presaleComplete);
          }
        })
      }

      setProducts((prevProducts) => {
        return {
          ...prevProducts,
          products: prevProducts.products.map((product) =>
            product.product_id === product_id
              ? { ...product, quantity: newQty }
              : product
          ),
        };
      });
  
      const apiEndpoint = `/api/update_quantity`;
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: id, quantity: newQty }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to update quantity');
      }

    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      // Refresh the cart in the Header component
      window.dispatchEvent(new Event("refreshCart"));
      //setUpdatingProduct(false);
    }
  };

	const handleAddToCart = async (product, selectedVariation = null, index = null) => {
    let sessionId = localStorage.getItem("session_id")
    
    if (!sessionId) {
      const session_id = uuid();
      localStorage.setItem("session_id", session_id)
      sessionId = session_id
    }

    // Request to add product to cart
    const response = await fetch("/api/add_product_to_cart", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: uuid(),
        session_id: sessionId,
        product_id: index ? product.variations[index] : product?.id,
        price: Number(product?.price),
        name: selectedVariation ? product?.name + " - " + selectedVariation : product?.name,
        image: product?.images[0].src,
        description: product?.description,
      }),
    });
  
    if (!response.ok) {
      console.log("failed to add to cart");
      throw new Error("Failed to add product to cart");
    }

    // Window Data Layer
    if (product) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          value: parseFloat(product.price),
          currency: 'COP',
          items: {
            item_id: product.product_id,
            item_name: product.name,
            price: parseFloat(product.price),
            quantity: product.quantity
          }
        }
      });
    }

    // Refresh the cart in the Header component
    window.dispatchEvent(new Event("refreshCart"));

    addToCart(
      index ? product.variations[index] : product?.id || 0,
      1,
      setCartWP,
      setIsAddedToCart,
      setLoading,
    )
  };

  return (
    <div>
        <div className="ml-4 flow-root lg:ml-6">
            <button onClick={() => setOpen(true)} className={`group -m-2 flex items-center p-2 ${scrolled ? 'text-white' : ''}`}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={30}
                    height={30}
                    fill="none"
                    stroke="#000"
                    viewBox="0 0 24 24"
                    >
                    <g stroke="#9CA3AF" strokeWidth={1.5}>
                    <path
                        strokeLinecap="round"
                        d="m2 3 .265.088c1.32.44 1.98.66 2.357 1.184C5 4.796 5 5.492 5 6.883V9.5c0 2.828 0 4.243.879 5.121.878.879 2.293.879 5.121.879h8"
                        />
                    <path d="M7.5 18a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM16.5 18a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM5 6h11.45c2.055 0 3.083 0 3.527.674.445.675.04 1.619-.77 3.508l-.428 1c-.378.882-.567 1.322-.942 1.57-.376.248-.856.248-1.815.248H5" />
                    </g>
                </svg>
                {cart?.totalQty > 0 ? (
                    <span className={`${manrope.className} text-sm font-medium z-auto rounded-[50%] bg-red-500 w-5 h-5 mb-4 -ml-3 text-[7px] text-white`}>{cart?.totalQty}</span>
                ) : ("")}
                <span className="sr-only">items in cart, view bag</span>
            </button>
        </div>
    <Dialog open={open} onClose={setOpen} className="relative z-40">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-50/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
        />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
              >
              <div className="flex h-full flex-col overflow-y-auto bg-white pt-[90px] lg:pt-[110px] mr-[40px] lg:mr-0 shadow-xl">
								<div className='h-auto'>
                <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                  <div className="flex items-start justify-between">
                    <DialogTitle className="text-lg font-medium uppercase text-gray-900">carrito de compra</DialogTitle>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="relative -m-2 p-2 text-gray-400 hover:text-gray-500"
                      >
                        <span className="absolute -inset-0.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="size-6" />
                      </button>
                    </div>
                  </div>
									{/* Section of empty cart */}
									{products.products.length > 0 ?
                  (<div className="mt-8">
                    <div className="flow-root">
                      <ul role="list" className="-my-6 divide-y divide-gray-200">
                        {products.products.map((product, idx) => (
                          <li key={idx} className="flex py-6">
                            <div className="size-24 shrink-0 overflow-hidden rounded-md bg-gray-100">
                              <img alt={product.name} src={product.image} className="size-full object-contain" />
                            </div>

                            <div className="ml-4 flex flex-1 flex-col">
                              <div>
                                <div className="flex justify-between text-base font-medium text-gray-900">
                                  <h3>
                                    <span>{product.name}</span>
                                  </h3>
                                  <p className="ml-4 whitespace-nowrap">$ {separadorDeMiles(Math.floor(product.price) * Number(product.quantity))}</p>
                                </div>
                              </div>
                              <div className="flex flex-1 items-end justify-between text-sm">
                                {/*Qty*/}
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {/* btn */}

                                    <div className="btn-aumentarDisminuir flex  h-[44px] w-[126px] items-center gap-1 px-[20px] py-[12px]">
                                    <button
                                        className="size-10 text-[25px] leading-10 text-gray-600 transition"
                                        onClick={(event) =>
                                        handleQtyChange(event, "decrement", product.quantity, product.product_id, product.id)
                                        }
                                    >
                                        -
                                    </button>
																		<div className='px-[10px] py-1 rounded-[4px] border border-gray-100'>
																			<span>{product.quantity}</span>
																		</div>
                                    <button
                                        className="size-10 text-[25px] leading-10 text-gray-600 transition"
                                        onClick={(event) =>
																					handleQtyChange(event, "increment", product.quantity, product.product_id, product.id)
                                        }
                                        >
                                        +
                                    </button>
                                    </div>

                                    {/*  */}
                                </div>

                                <div className="flex">
                                  <button onClick={(event) => handleRemoveProductClick(event, product.id, product.product_id)} type="button" className="text-black underline">
                                    ELIMINAR
                                  </button>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>) :
									(
										<div className='border-t border-gray-200 px-6 pt-2 mt-4 -mx-6'>
											<span className='text-[12px] lg:text-[14px] 2xl:text-[16px] whitespace-nowrap'>Su carrito actualmente está vacío.</span>
										</div>
									)
									}
                </div>

                {/* Suggestions section */}
                <div className="border-t justify-start h-auto border-gray-200 px-4 py-6 sm:px-6">
									<span className='text-[12px] font-bold mb-4 uppercase'>COMPLEMENTA TU COMPRA con ACCESORIOS MINCA</span>
                  <div className="flex justify-between text-base font-medium text-gray-900">
										<Carousel
											autoplay={false}
											loop={false}
											autoplayDelay={0}
											prevArrow={({ loop, handlePrev, firstIndex }) => {
												return (
													<button
														onClick={handlePrev}
														disabled={!loop && firstIndex}
														className="!absolute top-2/4 -left-3 -translate-y-2/4 rounded-[50%] select-none transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none w-12 max-w-[42px] h-12 max-h-[42px] text-white hover:bg-white/10 active:bg-white/30 grid place-items-center"
														>
														<ArrowsLeftHeaderPopUp />
													</button>
												);
											}}
											nextArrow={({ loop, handleNext, lastIndex }) => (
												<button
													onClick={handleNext}
													disabled={!loop && lastIndex}
													className="!absolute top-[30px] right-0 rounded-full select-none transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none w-12 max-w-[48px] h-12 max-h-[48px] text-white hover:bg-white/10 active:bg-white/30 grid place-items-center"
												>
													<ArrowRightHeaderPopUp />
												</button>
											)}
											className="overflow-y-hidden flex"
											>
											{accesorios
													.filter(accesorio => accesorio.stock_status !== "outofstock")
													.map((accesorio, index) => (
														<div key={index} className="h-auto flex items-center w-auto p-1">
															<div className='ml-6 border border-gray-200 rounded-[8px]'>
																<img
																	className="h-auto w-[100px]"
																	src={accesorio.images[0].src}
																	alt={accesorio.name}
																	/>
															</div>
															<div className='flex flex-col ml-6'>
																<p className="font-light text-[8px] lg:text-[10px] 2xl:text-[12px]">
																	{accesorio.name}
																</p>
																<div className="w-full">
																	<p className="text-[14px] leading-[20px] 2xl:text-[16px]">
																		{/* $ {accesorio?.price.includes(',') ? separadorDeMiles(accesorio?.price.slice(0, 6)) : accesorio?.price.replace(/[^\d.]/g, "")} */}
                                    $ {separadorDeMiles(accesorio?.price)}
																	</p>
																</div>
                                {accesorio?.type === "simple" &&
                                  <button onClick={() => handleAddToCart(accesorio)}>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width={24}
                                      height={24}
                                      fill="none"
                                      stroke="#000"
                                      viewBox="0 0 24 24"
                                      >
                                      <g stroke="#000" strokeWidth={1.5}>
                                      <path
                                          strokeLinecap="round"
                                          d="m2 3 .265.088c1.32.44 1.98.66 2.357 1.184C5 4.796 5 5.492 5 6.883V9.5c0 2.828 0 4.243.879 5.121.878.879 2.293.879 5.121.879h8"
                                          />
                                      <path d="M7.5 18a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM16.5 18a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM5 6h11.45c2.055 0 3.083 0 3.527.674.445.675.04 1.619-.77 3.508l-.428 1c-.378.882-.567 1.322-.942 1.57-.376.248-.856.248-1.815.248H5" />
                                      </g>
                                    </svg>
                                  </button>
                                }
                                <div className='flex items-center'>
                                {accesorio?.type === "variable" &&
                                  accesorio?.attributes[0].options.map((variation, idx) => {
                                    const isSelected = selectedVariations[accesorio.id] === variation;
                                    if (accesorio.id === 3646 && (variation === "M" || variation === "XL")) return
                                    return (
                                      <button
                                        key={idx}
                                        className={`rounded-full px-4 py-1 mr-2 text-[6px] lg:text-[8px] 2xl:text-[10px] ${
                                          isSelected ? "bg-black text-white" : "bg-[#FBFBFB] text-black"
                                        }`}
                                        onClick={() => {
                                          handleSelectVariation(accesorio.id, variation);
                                          handleAddToCart(accesorio, variation, idx);
                                        }}
                                      >
                                        {variation}
                                      </button>
                                    );
                                  })}
                                </div>
															</div>
														</div>
											))}
										</Carousel>
                  </div>
                </div>
								{products.products.length > 0 ? (
                <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
                  <div className="flex justify-between text-base font-medium text-gray-900">
                    <p className='uppercase'>Subtotal</p>
                    <p>$ {separadorDeMiles(price)}</p>
                  </div>
                  <p className="mt-0.5 text-sm text-black">Los gastos de envío e impuestos se calculan al finalizar la compra.</p>
                  <div className="mt-6">
                    <a
                      href="/cart"
                      className="flex items-center justify-center rounded-full border border-transparent bg-black px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-gray-600"
                      >
                      IR A PAGAR
                    </a>
                  </div>
                  <div className="mt-6 flex justify-center text-center text-black">
                    <p>
                      o{' '}
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="text-black"
                        >
                        <span className='underline'>Seguir comprando</span>
                        <span aria-hidden="true"> &rarr;</span>
                      </button>
                    </p>
                  </div>
                </div>) :
								(
									""
								)}
								</div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
    </div>
  )
}
